/**
 * URL helpers for the Phase 2 Context Switcher.
 *
 * All functions are pure (no React, no router) so they can be unit-tested
 * independently. They consume a `pathname` string + selection ids and
 * return the URL string the switcher should navigate to.
 */

/**
 * Subpaths that exist BOTH at the workspace level (e.g. /workspaces/W/calendar)
 * and at the film level (/workspaces/W/films/F/calendar). When the user
 * switches a film while sitting on an account- or workspace-level URL for
 * one of these subpaths, we drop them into the film-scoped equivalent.
 */
const HYBRID_SUBPATHS = ['calendar', 'gear', 'files'];

/**
 * Subpaths that exist ONLY at the film level (e.g. /workspaces/W/films/F/scenes).
 * When the user switches a film while already on one of these, we preserve
 * the subpath under the new film.
 */
const FILM_LEVEL_SUBPATHS = ['scenes', 'shots', 'board', 'manage', 'calendar', 'gear', 'files'];

/**
 * Compute the URL to navigate to when the user picks a different workspace.
 * Per Phase 2 spec: always route to that workspace's dashboard. The Phase 1
 * setter clears the film as a side-effect; we don't try to preserve any
 * subpath since the new workspace has its own film set.
 */
export function buildWorkspaceSwitchUrl(newWorkspaceId) {
  if (!newWorkspaceId) return '/';
  return `/workspaces/${newWorkspaceId}`;
}

/**
 * Compute the URL to navigate to when the user picks a different film.
 *
 * Behavior (per Phase 2 AC #4 and #5):
 *   1. If the user is already on a film-scoped page with a recognised top
 *      subpath (e.g. /workspaces/W/films/F/scenes), swap the film id and
 *      keep that subpath: → /workspaces/W/films/F2/scenes.
 *   2. If the user is on a DEEPER film-scoped page (e.g. scene detail
 *      /workspaces/W/films/F/scenes/S), drop the deep part and land on
 *      the new film's root: → /workspaces/W/films/F2.
 *   3. If the user is on an account-level hybrid page (/calendar, /gear,
 *      /files), promote them into the film-scoped equivalent under the
 *      new film: → /workspaces/W/films/F2/calendar.
 *   4. Same for workspace-level hybrid pages
 *      (/workspaces/W/calendar → /workspaces/W/films/F2/calendar).
 *   5. Otherwise — land on the new film's root.
 */
export function buildFilmSwitchUrl(pathname, newWorkspaceId, newFilmId) {
  const newRoot = `/workspaces/${newWorkspaceId}/films/${newFilmId}`;

  // ── workspace-scoped film URL ───────────────────────────────────────
  let m = pathname.match(/^\/workspaces\/[^/]+\/films\/[^/]+(\/.*)?$/);
  if (m) {
    const rest = (m[1] || '').replace(/^\//, '');
    if (rest === '') return newRoot;
    const first = rest.split('/')[0];
    if (FILM_LEVEL_SUBPATHS.includes(first) && rest === first) {
      return `${newRoot}/${first}`;
    }
    // Deeper than first level (e.g. .../scenes/S or .../shots/X/edit) → drop.
    return newRoot;
  }

  // ── legacy /films/:filmId/... URL ───────────────────────────────────
  m = pathname.match(/^\/films\/[^/]+(\/.*)?$/);
  if (m) {
    const rest = (m[1] || '').replace(/^\//, '');
    if (rest === '') return newRoot;
    const first = rest.split('/')[0];
    if (FILM_LEVEL_SUBPATHS.includes(first) && rest === first) {
      return `${newRoot}/${first}`;
    }
    return newRoot;
  }

  // ── account-level hybrid pages ──────────────────────────────────────
  const acctMatch = pathname.match(/^\/(calendar|gear|files)$/);
  if (acctMatch) {
    return `${newRoot}/${acctMatch[1]}`;
  }

  // ── workspace-level hybrid pages ────────────────────────────────────
  const wsMatch = pathname.match(/^\/workspaces\/[^/]+\/(calendar|gear|files)$/);
  if (wsMatch) {
    return `${newRoot}/${wsMatch[1]}`;
  }

  // ── default: film root ──────────────────────────────────────────────
  return newRoot;
}

/**
 * Compute the URL to navigate to when the user picks a different scene
 * from the scene chip. We try to preserve the URL structure (legacy
 * /scenes/:sceneId, or any future /workspaces/W/films/F/scenes/:sceneId
 * pattern) and just swap the scene id segment.
 *
 * Returns null if we cannot find a scene id segment in the pathname —
 * which means the chip should not have been rendered in the first place
 * and the caller can fall back to a sensible default.
 */
export function buildSceneSwitchUrl(pathname, newSceneId) {
  if (!newSceneId) return null;

  // /workspaces/W/films/F/scenes/:sceneId[/suffix...]
  let m = pathname.match(/^(\/workspaces\/[^/]+\/films\/[^/]+\/scenes\/)([^/]+)(.*)$/);
  if (m) return `${m[1]}${newSceneId}${m[3]}`;

  // /films/F/scenes/:sceneId[/suffix...]
  m = pathname.match(/^(\/films\/[^/]+\/scenes\/)([^/]+)(.*)$/);
  if (m) return `${m[1]}${newSceneId}${m[3]}`;

  // Legacy /scenes/:sceneId[/suffix...]
  m = pathname.match(/^(\/scenes\/)([^/]+)(.*)$/);
  if (m) return `${m[1]}${newSceneId}${m[3]}`;

  return null;
}

/**
 * Same shape as buildSceneSwitchUrl but for shot ids.
 */
export function buildShotSwitchUrl(pathname, newShotId) {
  if (!newShotId) return null;

  let m = pathname.match(/^(\/workspaces\/[^/]+\/films\/[^/]+\/.*\/shots\/)([^/]+)(.*)$/);
  if (m) return `${m[1]}${newShotId}${m[3]}`;

  m = pathname.match(/^(\/films\/[^/]+\/.*\/shots\/)([^/]+)(.*)$/);
  if (m) return `${m[1]}${newShotId}${m[3]}`;

  m = pathname.match(/^(\/shots\/)([^/]+)(.*)$/);
  if (m) return `${m[1]}${newShotId}${m[3]}`;

  return null;
}

/**
 * Extract any :sceneId or :shotId from the URL so the switcher can decide
 * whether to render the Scene / Shot chips and which one is selected.
 */
export function extractDeepIds(pathname) {
  const sceneId =
    pathname.match(/\/scenes\/([^/]+)/)?.[1] ||
    null;
  const shotId =
    pathname.match(/\/shots\/([^/]+)/)?.[1] ||
    null;
  return { sceneId, shotId };
}

export const __TEST_ONLY__ = { HYBRID_SUBPATHS, FILM_LEVEL_SUBPATHS };
